import React, { useState, useCallback, useEffect } from 'react';
import { 
  Box, TextField, Button, Typography, Paper, Container, 
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  FormControl, InputLabel, Select, MenuItem, Chip, Divider, IconButton, Tooltip, Stack, CircularProgress, InputAdornment, Checkbox, ListItemText
} from '@mui/material';
import axios from '../../api/axiosConfig';
import PeopleIcon from '@mui/icons-material/People';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import GroupWorkIcon from '@mui/icons-material/GroupWork';

const UserManage = () => {
  const API_URL = '/api/admin/users';
  const GROUP_API_URL = '/api/admin/groups';
  const THEME_NAVY = '#1a233a';
  
  const [users, setUsers] = useState([]);
  const [allGroups, setAllGroups] = useState([]); 
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedUserPk, setSelectedUserPk] = useState(null);

  const [filterText, setFilterText] = useState('');
  const [filterRole, setFilterRole] = useState('ALL');

  const [formData, setFormData] = useState({
    id: '',
    password: '',
    name: '',
    role: 'STUDENT',
    groupPks: [] // 단일 groupPk에서 다중 선택을 위한 배열 구조로 변경
  });

  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const response = await axios.get(GROUP_API_URL);
        setAllGroups(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error("그룹 목록 로드 실패:", error);
      }
    };
    fetchGroups();
  }, []);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setHasSearched(true);
    try {
      const response = await axios.get(API_URL, {
        params: {
          search: filterText,
          role: filterRole === 'ALL' ? '' : filterRole
        }
      });
      setUsers(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("사용자 로딩 실패:", error);
    } finally {
      setLoading(false);
    }
  }, [filterText, filterRole]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    // 멀티 셀렉트와 일반 입력 필드 동시 처리
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isEditMode) {
        await axios.put(`${API_URL}/${selectedUserPk}`, formData);
        alert('사용자 정보가 수정되었습니다.');
      } else {
        if (!formData.password) return alert('비밀번호를 입력해주세요.');
        await axios.post(API_URL, formData);
        alert('신규 사용자가 등록되었습니다.');
      }
      resetForm();
      fetchUsers();
    } catch (error) {
      alert('처리 중 오류가 발생했습니다.');
    }
  };

  const handleEdit = (user) => {
    setIsEditMode(true);
    setSelectedUserPk(user.userPk);
    setFormData({
      id: user.id,
      password: '',
      name: user.name,
      role: user.role,
      // 백엔드에서 user 객체에 groupPks 리스트를 담아준다고 가정
      groupPks: user.groupPks || [] 
    });
  };

  const handleDelete = async (userPk) => {
    if (window.confirm('이 사용자를 삭제하시겠습니까?')) {
      try {
        await axios.delete(`${API_URL}/${userPk}`);
        fetchUsers();
      } catch (error) {
        alert('삭제 실패');
      }
    }
  };

  const resetForm = () => {
    setIsEditMode(false);
    setSelectedUserPk(null);
    setFormData({ id: '', password: '', name: '', role: 'STUDENT', groupPks: [] });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour12: false });
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <PeopleIcon sx={{ mr: 1, color: THEME_NAVY, fontSize: 30 }} />
        <Typography variant="h5" sx={{ fontWeight: 'bold', color: THEME_NAVY }}>사용자 계정 관리</Typography>
      </Box>

      <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', lg: 'row' } }}>
        <Paper elevation={3} sx={{ p: 3, width: { xs: '100%', lg: '350px' }, height: 'fit-content', borderTop: isEditMode ? '4px solid #ed6c02' : `4px solid ${THEME_NAVY}`, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', fontWeight: 'bold' }}>
              {isEditMode ? <EditIcon sx={{ mr: 1 }} color="warning" /> : <AddCircleOutlineIcon sx={{ mr: 1 }} color="primary" />}
              {isEditMode ? '사용자 수정' : '신규 사용자 등록'}
            </Typography>
            {isEditMode && <IconButton size="small" onClick={resetForm}><CloseIcon /></IconButton>}
          </Box>
          <Divider sx={{ mb: 2 }} />
          
          <form onSubmit={handleSubmit}>
            <TextField fullWidth label="아이디" name="id" value={formData.id} onChange={handleChange} margin="normal" required size="small" disabled={isEditMode} />
            <TextField fullWidth label={isEditMode ? "비밀번호 (변경 시 입력)" : "비밀번호"} name="password" type="password" value={formData.password} onChange={handleChange} margin="normal" size="small" required={!isEditMode} />
            <TextField fullWidth label="이름" name="name" value={formData.name} onChange={handleChange} margin="normal" required size="small" />
            
            <FormControl fullWidth margin="normal" size="small">
              <InputLabel>권한 부여</InputLabel>
              <Select name="role" value={formData.role} label="권한 부여" onChange={handleChange}>
                <MenuItem value="STUDENT">STUDENT (일반 학생)</MenuItem>
                <MenuItem value="TEACHER">TEACHER (교사)</MenuItem>
                <MenuItem value="ADMIN">ADMIN (시스템 관리자)</MenuItem>
              </Select>
            </FormControl>

            {/* 개선된 그룹 멀티 선택 필드 */}
            <FormControl fullWidth margin="normal" size="small">
              <InputLabel>소속 그룹 선택</InputLabel>
              <Select
                multiple
                name="groupPks"
                value={formData.groupPks}
                label="소속 그룹 선택"
                onChange={handleChange}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.length === 0 && <Typography variant="body2" color="text.secondary">미지정</Typography>}
                    {selected.map((value) => {
                      const group = allGroups.find(g => g.groupPk === value);
                      return <Chip key={value} label={group?.name || value} size="small" sx={{ height: 20, fontSize: '0.65rem' }} />;
                    })}
                  </Box>
                )}
              >
                {allGroups.map((group) => (
                  <MenuItem key={group.groupPk} value={group.groupPk}>
                    <Checkbox checked={formData.groupPks.indexOf(group.groupPk) > -1} size="small" />
                    <ListItemText primary={group.name} primaryTypographyProps={{ fontSize: '0.85rem' }} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button fullWidth variant="contained" type="submit" sx={{ mt: 3, py: 1.2, backgroundColor: isEditMode ? '#ed6c02' : THEME_NAVY, fontWeight: 'bold' }}>
              {isEditMode ? '정보 수정하기' : '사용자 계정 생성'}
            </Button>
          </form>
        </Paper>

        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Paper elevation={1} sx={{ p: 2, mb: 2, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', backgroundColor: '#fdfdfd', borderRadius: 2 }}>
            <TextField 
              size="small" placeholder="아이디 또는 이름 검색" 
              value={filterText} onChange={(e) => setFilterText(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && fetchUsers()} sx={{ minWidth: '250px' }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            />
            <FormControl size="small" sx={{ minWidth: '150px' }}>
              <InputLabel>권한 필터</InputLabel>
              <Select label="권한 필터" value={filterRole} onChange={(e) => setFilterRole(e.target.value)}>
                <MenuItem value="ALL">전체 권한</MenuItem>
                <MenuItem value="STUDENT">STUDENT</MenuItem>
                <MenuItem value="TEACHER">TEACHER</MenuItem>
                <MenuItem value="ADMIN">ADMIN</MenuItem>
              </Select>
            </FormControl>
            <Button variant="contained" startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />} onClick={fetchUsers} sx={{ bgcolor: THEME_NAVY }}>조회</Button>
            <Button startIcon={<RefreshIcon />} onClick={() => { setFilterText(''); setFilterRole('ALL'); setHasSearched(false); setUsers([]); }}>초기화</Button>
          </Paper>

          <TableContainer component={Paper} elevation={3} sx={{ maxHeight: 'calc(100vh - 405px)', overflow: 'auto' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', py: 1.5, bgcolor: '#f4f6f8' }}>사용자 정보</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: '#f4f6f8' }}>권한 / 소속 그룹</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f4f6f8' }}>등록 정보</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f4f6f8' }}>수정 정보</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: '#f4f6f8' }}>액션</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={5} align="center" sx={{ py: 10 }}><CircularProgress /></TableCell></TableRow>
                ) : !hasSearched ? (
                  <TableRow><TableCell colSpan={5} align="center" sx={{ py: 15 }}><InfoOutlinedIcon sx={{ fontSize: 40, color: 'grey.300', mb: 1 }} /><Typography color="text.secondary" fontWeight="bold">조회 버튼을 클릭해주세요.</Typography></TableCell></TableRow>
                ) : users.length === 0 ? (
                  <TableRow><TableCell colSpan={5} align="center" sx={{ py: 10 }}>검색 결과가 없습니다.</TableCell></TableRow>
                ) : (
                  users.map((user) => (
                    <TableRow key={user.userPk} hover sx={{ backgroundColor: selectedUserPk === user.userPk ? '#fff4e5' : 'inherit' }}>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 'bold', color: THEME_NAVY }}>{user.name}</Typography>
                          <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
                            <FingerprintIcon sx={{ fontSize: 14, mr: 0.3 }} /> ID: {user.id}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Stack spacing={0.5} alignItems="center">
                          <Chip label={user.role} size="small" color={user.role === 'ADMIN' ? 'secondary' : user.role === 'TEACHER' ? 'primary' : 'default'} sx={{ fontWeight: 'bold', fontSize: '0.7rem' }} />
                          {/* 여러 그룹 이름을 나열하도록 수정 */}
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.3, justifyContent: 'center' }}>
                            {user.groupNames && user.groupNames.length > 0 ? (
                              user.groupNames.map((gName, idx) => (
                                <Typography key={idx} variant="caption" sx={{ display: 'flex', alignItems: 'center', color: 'primary.main', fontWeight: 'bold' }}>
                                  <GroupWorkIcon sx={{ fontSize: 10, mr: 0.2 }} /> {gName}
                                </Typography>
                              ))
                            ) : (
                              <Typography variant="caption" color="text.disabled">소속 없음</Typography>
                            )}
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" display="block" sx={{ fontWeight: 'bold' }}>{user.createUser || 'System'}</Typography>
                        <Typography variant="caption" color="text.secondary">{formatDateTime(user.createDate)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" display="block" sx={{ fontWeight: 'bold' }}>{user.updateUser || '-'}</Typography>
                        <Typography variant="caption" color="text.secondary">{user.updateDate ? formatDateTime(user.updateDate) : '-'}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Stack direction="row" justifyContent="center">
                          <Tooltip title="정보 수정"><IconButton size="small" color="primary" onClick={() => handleEdit(user)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                          <Tooltip title="계정 삭제"><IconButton size="small" color="error" onClick={() => handleDelete(user.userPk)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>
    </Container>
  );
};

export default UserManage;